import { memo, useCallback } from 'react';
import { Checkbox, RadioButton } from '@aimsos/ui';

/**
 * Card with a checkbox or a radio on the left; the whole card toggles it. Shared by the user
 * type, role and group choices of the invite builder.
 *
 * @param {object} props
 * @param {'checkbox'|'radio'} [props.type='checkbox']
 * @param {boolean} props.selected
 * @param {string} props.ariaLabel Accessible name of the control.
 * @param {string} props.value Passed back to `onSelect`.
 * @param {(value: string) => void} props.onSelect
 * @param {import('react').ReactNode} props.children Card content.
 */
function SelectableCard({ type = 'checkbox', selected, ariaLabel, value, onSelect, children }) {
  const handleClick = useCallback(() => onSelect(value), [onSelect, value]);
  const stop = useCallback((event) => event.stopPropagation(), []);

  return (
    <div
      onClick={handleClick}
      className={[
        'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
        selected
          ? 'border-accent/60 bg-accent/5'
          : 'border-edge bg-surface hover:border-accent/40',
      ].join(' ')}
    >
      {/* The control handles its own click; stopPropagation avoids toggling twice. */}
      <span className="mt-0.5 shrink-0" onClick={stop}>
        {type === 'radio' ? (
          <RadioButton checked={selected} aria-label={ariaLabel} onChange={handleClick} />
        ) : (
          <Checkbox
            size="small"
            checked={selected}
            aria-label={ariaLabel}
            onChange={handleClick}
          />
        )}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export default memo(SelectableCard);
